/**
 * How often, and how many times, CF may ask the native host for a token — and how long each
 * of the things that waits on a reply should wait.
 *
 * Lives in the Auth service rather than beside the layout gate that consumes it, because every
 * value here is a property of the NATIVE ROUND-TRIP, not of any screen. It was originally
 * exported from `components/ui/layouts/auth-loading-gate.logic`, which meant
 * `Auth.store → WebView.auth → webview-auth-reply.logic → components/ui/layouts/…`: the auth
 * store transitively depending on the component layer, so any future component import added to
 * that file would silently become a dependency of authentication itself.
 */

/** Max times we ask native for a fresh token before we stop and render anyway. */
export const MAX_WEBVIEW_AUTH_RECOVERIES = 2;

/**
 * Minimum gap between token requests, sized to the NATIVE round-trip rather than to
 * how fast we'd like to retry.
 *
 * Native answers `REQUEST_AUTH_TOKEN` via `authService.checkAndRefreshToken()`, which
 * makes a real refresh API call whenever the token is within an hour of expiring —
 * i.e. exactly the population this recovery exists for. Retrying faster than that
 * round-trip has two costs, both of which make things worse rather than better:
 *
 *  - Every attempt is spent before the first reply can land, so the bound is reached
 *    on timing alone and an authenticated user sees the login form flash first.
 *  - `use-webview-auth` already sends one request on mount, so a fast cadence stacks
 *    3 requests in a few seconds → 3 concurrent `refreshToken()` calls, each writing
 *    the keychain, with no in-flight dedupe on the mobile side. If refresh tokens
 *    rotate server-side, those calls can invalidate each other.
 */
export const RECOVERY_REPLY_WINDOW_MS = 5000;

/**
 * The FIRST attempt waits longer than the reply window, because it is not the first
 * request native receives: `use-webview-auth` fires one on mount. This leaves that
 * request a full window to complete before we add another.
 */
export const FIRST_RECOVERY_DELAY_MS = 8000;

/**
 * How long to wait before spending the next recovery attempt.
 *
 * Keyed on the attempt index, not on which stall arm fired: both arms recover the same
 * way — by asking native for a token — so both are bound by the same round-trip and
 * neither can usefully go faster.
 *
 * Timings, and be honest about which arm they hold on. The timer cannot start until
 * `attemptRecovery` is true, and `brandsFetching` blocks that while a request is in
 * flight, so the arms differ:
 *  - HANDSHAKE arm (the dominant incident path, brands disabled and idle): requests at
 *    ~0s / ~8s / ~13s, terminal screen ~13s.
 *  - SERVER-REJECTED arm (401/403): Http intercepts the 401 and polls up to
 *    TOKEN_REFRESH_WAIT_MS (5s) before the query settles to isError, so attempt 1 lands ~13s
 *    and the terminal render is ~18s+. Slower than the handshake arm, still bounded, still
 *    terminal.
 *  - NON-AUTH arm (5xx, network): no longer waits for this budget at all. React Query retries
 *    3x (~7s), then shouldShowWebviewAuthError treats it as terminal immediately and recovery
 *    declines it, so the screen renders at ~7-8s with no reload.
 *  - OFFLINE arm: terminal immediately, no attempt spent — see shouldShowWebviewAuthError.
 *
 * Do not read these numbers as "inside native's 20s PAGE_READY fallback" without checking the
 * native side: that timer is armed in `handleLoadEnd`, which early-returns when the reloaded
 * URL has the same base (mobile-app WebviewWrapper.tsx) — and a recovery reload frequently
 * does. The terminal screen signals PAGE_READY itself, which is what actually lifts it.
 */
export const getRecoveryDelayMs = (attempts: number): number =>
  attempts <= 0 ? FIRST_RECOVERY_DELAY_MS : RECOVERY_REPLY_WINDOW_MS;

/**
 * How long the manual Retry waits for native before admitting failure.
 *
 * Derived from the reply window, not an independent number, because it waits on the same
 * native round-trip — a refresh API call when the token is near expiry. An independent
 * constant invites exactly the bug the auto-cadence had: give up before the reply can
 * land and tell the user to close the app while it was about to succeed. Two windows is
 * headroom for a slow mobile network.
 *
 * Exported (rather than inlined in the screen) so the test asserts against THIS value instead
 * of re-deriving its own copy — a test comparing two copies of the same expression stays green
 * no matter how far the real constant drifts.
 */
export const RETRY_FEEDBACK_MS = RECOVERY_REPLY_WINDOW_MS * 2;
